import { UAEntityTypes } from '@unifyapps/defs/types/entities';
import type {
  WalkthroughSession,
  WalkthroughSessionEntity,
} from '@unifyapps/defs/types/walkthroughSession';
import { useCallback } from 'react';
import { useWalkthroughManagerStore, WalkthroughMode } from '../../stores/WalkthroughManagerStore';
import {
  useCreateWalkthroughSession,
  useUpdateWalkthroughSession,
} from '../data/walkthroughSession';

type CreateParams = {
  walkthroughId: string;
  interfaceId: string;
  sessionId?: string;
};

/**
 * abstracts session creation/update so builder/preview can run without backend,
 * while runner continues to use real mutations.
 */
export function useWalkthroughSessionClient() {
  const mode = useWalkthroughManagerStore().hooks.useMode();
  const { createWalkthroughSession: createWalkthroughSessionMutation } =
    useCreateWalkthroughSession();
  const { updateWalkthroughSession: updateWalkthroughSessionMutation } =
    useUpdateWalkthroughSession();

  const buildLocalSession = useCallback(
    ({
      walkthroughId,
      interfaceId,
      status = 'in_progress',
      sessionId,
      currentStepId,
      completedStepIds = [],
    }: {
      walkthroughId: string;
      interfaceId: string;
      status?: WalkthroughSession['status'];
      sessionId?: string;
      currentStepId?: string;
      completedStepIds?: string[];
    }): WalkthroughSessionEntity => {
      const timestamp = Date.now();
      return {
        id: `local-walkthrough-session-${walkthroughId}`,
        entityType: UAEntityTypes.WalkthroughSession,
        createdTime: timestamp,
        modifiedTime: timestamp,
        properties: {
          walkthroughId,
          interfaceId,
          status,
          sessionId,
          currentStepId,
          completedStepIds,
        },
      };
    },
    [],
  );

  const createSession = useCallback(
    async ({ walkthroughId, interfaceId, sessionId }: CreateParams) => {
      if (mode !== WalkthroughMode.RUNNER) {
        return buildLocalSession({ walkthroughId, interfaceId, sessionId });
      }

      const session = await createWalkthroughSessionMutation({
        walkthroughId,
        interfaceId,
        sessionId,
      });

      return session ?? buildLocalSession({ walkthroughId, interfaceId, sessionId });
    },
    [mode, buildLocalSession, createWalkthroughSessionMutation],
  );

  const updateSession = useCallback(
    async (session: WalkthroughSessionEntity) => {
      if (mode !== WalkthroughMode.RUNNER) {
        return session;
      }

      const updatedSession = await updateWalkthroughSessionMutation(session);
      return updatedSession ?? session;
    },
    [mode, updateWalkthroughSessionMutation],
  );

  return {
    createSession,
    updateSession,
    buildLocalSession,
  };
}
